/**
 * Arabic message catalog. Same keys as en.ts; plurals carry all six Arabic
 * forms (`_zero`, `_one`, `_two`, `_few`, `_many`, `_other`).
 *
 * Terms are the glossary's (docs/help/glossary.md); error copy is the error
 * catalog's (docs/help/errors.md) wherever it has the message. Keys listed in
 * AR_DRAFT_KEYS were written by engineering in the glossary's terms because no
 * approved copy exists yet: ux-writer reviews them and removes each key from
 * the list once approved. test/i18n.test.tsx fails if a listed key does not
 * exist, so the list cannot drift from the catalog.
 *
 * Arabic is completeness-checked by test, not by type, because its plural keys
 * differ from English (test/i18n.test.tsx, "catalog completeness").
 */
import type { Catalog } from "../types";
import { CONTROLS_AR_DRAFT_KEYS, controlsAr } from "./controls.ar";
import { SCREENS_AR_DRAFT_KEYS, screensAr } from "./screens.ar";

export const ar = {
  common: {
    tryAgain: "إعادة المحاولة",
    details: "التفاصيل",
    chooseCountry: "اختر دولة",
    cancel: "إلغاء",
    save: "حفظ",
    saving: "جارٍ الحفظ…",
    create: "إنشاء",
    select: "اختر…",
    languageNames: {
      en: "الإنجليزية",
      ar: "العربية",
    },
  },
  meta: {
    title: "{{product}} لتخطيط موارد المؤسسات",
    description:
      "{{product}} — نظام سحابي لتخطيط موارد المؤسسات، جاهز للتشغيل الفعلي في أكثر من دولة. اضبطه دون برمجة، وتحكّم فيه دون تعطيل العمل، واعرف دائماً سبب ما يحدث.",
  },
  ui: {
    breadcrumb: "مسار التنقل",
    howToFix: "طريقة الحل:",
    requiredNote: "الحقول المميزة بعلامة النجمة مطلوبة.",
  },
  crash: {
    title: "حدث خطأ من جانبنا",
    body: "أعد المحاولة، وإن تكرر ذلك فتواصل مع الدعم.",
  },
  resource: {
    loadFailed: "تعذّر تحميل {{title}}",
  },
  records: {
    types: {
      PurchaseOrder: "أمر الشراء",
      SalesOrder: "أمر البيع",
      SalesInvoice: "فاتورة المبيعات",
      JournalEntry: "قيد اليومية",
      Customer: "العميل",
      Supplier: "المورد",
      Product: "الصنف",
      SupplierInvoice: "فاتورة المورد",
      GoodsReceipt: "إذن الاستلام",
      Payment: "الدفعة",
      InventoryMovement: "الحركة المخزنية",
      ProductionOrder: "أمر الإنتاج",
      SalesOrderDelivery: "تسليم أمر البيع",
    },
  },
  status: {
    JournalEntry: {
      Draft: "مسودة",
      Submitted: "بانتظار الاعتماد",
      Approved: "معتمد",
      Posted: "مُرحَّل",
      Reversed: "معكوس",
      Voided: "ملغى",
    },
    SalesOrder: {
      Draft: "مسودة",
      Confirmed: "مؤكَّد",
      Approved: "معتمد",
      Released: "مُحال للتنفيذ",
      Fulfilled: "تم التجهيز",
      Delivered: "تم التسليم",
      Invoiced: "صدرت فاتورته",
      Completed: "مكتمل",
      Cancelled: "ملغى",
    },
    SalesInvoice: {
      Draft: "مسودة",
      Approved: "معتمدة",
      Posted: "مُرحَّلة",
      Sent: "مُرسَلة للعميل",
      Paid: "مُسدَّدة",
      Overdue: "متأخرة السداد",
      WrittenOff: "مُعدَمة",
    },
    PurchaseOrder: {
      Draft: "مسودة",
      Approved: "معتمد",
      Sent: "مُرسَل للمورد",
      Confirmed: "مؤكَّد من المورد",
      PartiallyReceived: "مُستلَم جزئياً",
      Received: "مُستلَم بالكامل",
      Closed: "مُغلَق",
      Cancelled: "ملغى",
    },
    Customer: {
      Active: "نشط",
      Blocked: "موقوف",
      Dormant: "خامل",
    },
    Supplier: {
      Active: "نشط",
      Blocked: "موقوف",
      Dormant: "خامل",
    },
    Product: {
      Draft: "مسودة",
      Active: "نشط",
      Obsolete: "متوقف",
    },
    // C7-REST. Workspace, AccountingPeriod and InventoryMovement are the
    // glossary's status table; the rest agree in gender with the record
    // (glossary.md, after the table): كيان، فرع، موقع، كشف، أصل، إقرار، بند،
    // إذن، مردود، خصم، طلب، عقد، تحويل، جرد، تطبيق، مستخدم، دور، نموذج،
    // تخطيط، موصل، اشتراك are masculine; شركة، موازنة، مجموعة، عمولة،
    // تسوية، رسالة، طريقة عرض، مصفوفة، عملية استيراد، فاتورة are feminine.
    Workspace: {
      Provisioning: "قيد التجهيز",
      Active: "نشطة",
      Suspended: "موقوفة",
      LegalHold: "محتجزة لأسباب قانونية",
      Closed: "مُغلَقة",
    },
    AccountingPeriod: {
      Open: "مفتوحة",
      Closing: "قيد الإقفال",
      Closed: "مُقفَلة",
    },
    InventoryMovement: {
      Draft: "مسودة",
      Posted: "مُرحَّلة",
    },
    LegalEntity: {
      Provisional: "مبدئي",
      Active: "نشط",
      Inactive: "غير نشط",
    },
    Company: {
      Draft: "مسودة",
      Active: "نشطة",
      Closed: "مُغلَقة",
    },
    Branch: {
      Active: "نشط",
      Inactive: "غير نشط",
    },
    Location: {
      Active: "نشط",
      Inactive: "غير نشط",
    },
    BankStatement: {
      Uploaded: "مرفوع",
      Matched: "مُطابَق",
      Posted: "مُرحَّل",
    },
    FixedAsset: {
      Draft: "مسودة",
      Active: "قيد الاستخدام",
      Depreciated: "مُهلَك بالكامل",
      Disposed: "مُستبعَد",
      Impairment: "منخفض القيمة",
    },
    TaxReturn: {
      Draft: "مسودة",
      Filed: "مُقدَّم",
      Overdue: "متأخر",
      Paid: "مُسدَّد",
    },
    Budget: {
      Draft: "مسودة",
      Active: "سارية",
      Superseded: "مُستبدَلة",
    },
    ConsolidationGroup: {
      NotRun: "لم تُوحَّد بعد",
      Completed: "مكتملة",
      Failed: "فشلت",
    },
    IntercompanyItem: {
      Open: "مفتوح",
      Matched: "مُطابَق",
      Settled: "مُسوّى",
    },
    DeliveryNote: {
      Draft: "مسودة",
      Picking: "قيد التجميع",
      Packed: "مُعبَّأ",
      Shipped: "مشحون",
      Delivered: "تم التسليم",
      Partial: "مُسلَّم جزئياً",
      Returned: "مُرتجَع",
    },
    SalesReturn: {
      Requested: "مطلوب",
      Approved: "معتمد",
      Received: "مُستلَم",
      Rejected: "مرفوض",
      Credited: "صدر عنه إشعار دائن",
      Replaced: "تم الاستبدال",
    },
    Discount: {
      Active: "ساري",
      Draft: "مسودة",
      Superseded: "مُستبدَل",
    },
    CreditLimitRequest: {
      Pending: "قيد المراجعة",
      Approved: "معتمد",
      Declined: "مرفوض",
    },
    Commission: {
      Accrued: "مستحقة",
      Approved: "معتمدة",
      Paid: "مُسدَّدة",
    },
    PurchaseRequest: {
      Draft: "مسودة",
      Submitted: "بانتظار الاعتماد",
      Approved: "معتمد",
      Rejected: "مرفوض",
      Converted: "حُوِّل إلى أمر شراء",
      Cancelled: "ملغى",
    },
    Rfq: {
      Draft: "مسودة",
      Sent: "مُرسَل للموردين",
      QuotesReceived: "وردت العروض",
      Awarded: "تمت الترسية",
      Cancelled: "ملغى",
    },
    ProcurementContract: {
      Active: "ساري",
      Expiring: "يقترب من الانتهاء",
      Expired: "منتهي",
      Draft: "مسودة",
      Cancelled: "ملغى",
    },
    StockTransfer: {
      Draft: "مسودة",
      InTransit: "في الطريق",
      Received: "مُستلَم",
      Posted: "مُرحَّل",
      Cancelled: "ملغى",
    },
    StockAdjustment: {
      Draft: "مسودة",
      PendingApproval: "بانتظار الاعتماد",
      Approved: "معتمدة",
      Posted: "مُرحَّلة",
      Rejected: "مرفوضة",
    },
    CycleCount: {
      Draft: "مسودة",
      InProgress: "جارٍ",
      Reviewed: "تمت مراجعته",
      Approved: "معتمد",
      Posted: "مُرحَّل",
    },
    ApiClient: {
      Active: "نشط",
      Revoked: "ملغى",
    },
    OutboxMessage: {
      Pending: "بانتظار الإرسال",
      Sending: "جارٍ إرسالها",
      Sent: "مُرسَلة",
      DeadLetter: "تعذّر تسليمها",
    },
    TenantUser: {
      Invited: "مدعو",
      Active: "نشط",
      Suspended: "موقوف",
      Removed: "محذوف",
    },
    Role: {
      Draft: "مسودة",
      Active: "نشط",
      Superseded: "مُستبدَل",
    },
    EntityDefinition: {
      Draft: "مسودة",
      Validated: "تم التحقق منه",
      Simulated: "تمت محاكاته",
      Approved: "معتمد",
      Active: "نشط",
      Superseded: "مُستبدَل",
    },
    ViewDefinition: {
      Draft: "مسودة",
      Active: "نشطة",
      Superseded: "مُستبدَلة",
    },
    LayoutDefinition: {
      Draft: "مسودة",
      Active: "نشط",
    },
    ApprovalMatrix: {
      Draft: "مسودة",
      Active: "نشطة",
      Superseded: "مُستبدَلة",
    },
    Connector: {
      Connected: "متصل",
      Error: "به خطأ",
      NotConfigured: "غير مُهيَّأ",
      Disabled: "معطَّل",
    },
    ImportJob: {
      Uploaded: "مرفوعة",
      Validating: "جارٍ التحقق",
      Previewing: "جارٍ العرض المبدئي",
      DryRun: "تشغيل تجريبي",
      Completed: "مكتملة",
      Failed: "فشلت",
      RolledBack: "تم التراجع عنها",
    },
    Subscription: {
      Active: "نشط",
      PastDue: "متأخر السداد",
      Cancelled: "ملغى",
      Trial: "تجريبي",
    },
    SubscriptionInvoice: {
      Paid: "مُسدَّدة",
      Open: "مفتوحة",
      Overdue: "متأخرة السداد",
    },
  },
  money: {
    othersNotConverted: "وكذلك {{amounts}}، دون تحويل",
    currency: "العملة",
    chooseCurrency: "اختر عملة",
    currencyHint: "عملة مبالغ هذا السجل. تبدأ بعملة مساحة العمل.",
    allCurrencies: "كل العملات",
    fromCurrency: "من",
    toCurrency: "إلى",
  },
  books: {
    legalEntity: "الكيان القانوني",
    chooseLegalEntity: "اختر الكيان القانوني",
    entityOption: "{{name}} ({{currency}})",
    legalEntityRequired:
      "تمسك مساحة العمل هذه دفاترها بأكثر من عملة، لذلك يعرض التقرير دفاتر كيان قانوني واحد في كل مرة. اختر الكيان القانوني.",
  },
  shell: {
    primaryNav: "القائمة الرئيسية",
    toggleNav: "إظهار القائمة أو إخفاؤها",
    commands: "الأوامر",
    themeToLight: "التبديل إلى المظهر الفاتح",
    themeToDark: "التبديل إلى المظهر الداكن",
    noWorkspace: "لا توجد مساحة عمل",
    cell: "الخلية: {{cell}}",
    palette: {
      label: "لوحة الأوامر",
      placeholder: "الأوامر والتنقل والسجلات…",
      noResults: "لا توجد نتائج مطابقة لـ «{{query}}».",
    },
  },
  session: {
    ended: "انتهت جلستك. يُرجى تسجيل الدخول مرة أخرى.",
    workspaceCreated: "تم إنشاء مساحة العمل. سجّل الدخول مرة أخرى لفتحها.",
    workspaceCreatedMultiple:
      "تم إنشاء مساحة العمل. سجّل الدخول مرة أخرى. أنت عضو في أكثر من مساحة عمل، ويفتح {{product}} أولها بالترتيب الأبجدي، ولا يمكن بعدُ اختيار المساحة التي تُفتح.",
    mfaRequired:
      "أصبح التحقق بخطوتين إلزامياً لك في مساحة العمل هذه. سجّل الدخول مرة أخرى لإعداده، فتُفتح مساحة العمل فور تأكيده.",
    signOut: "تسجيل الخروج",
    signingOut: "جارٍ تسجيل الخروج…",
    checkAgain: "إعادة التحقق",
    checking: "جارٍ التحقق…",
    stillUnavailable: "ما زالت مساحة العمل غير متاحة. أعد المحاولة بعد قليل.",
    workspaceBlock: {
      suspended: {
        title: "مساحة العمل هذه موقوفة",
        body: "لا يمكن استخدامها في الوقت الحالي. تواصل مع مالك مساحة العمل أو مع دعم {{product}}.",
      },
      provisioning: {
        title: "ما زالت مساحة العمل قيد التجهيز",
        body: "يستغرق ذلك عادةً لحظات. انتظر قليلاً ثم اختر «إعادة التحقق»، ولا حاجة إلى تسجيل الدخول مرة أخرى.",
      },
      legalHold: {
        title: "مساحة العمل هذه محتجزة لأسباب قانونية",
        body: "لا يمكن استخدامها حالياً. تواصل مع مالك مساحة العمل أو مع دعم {{product}}.",
      },
      closed: {
        title: "أُغلقت مساحة العمل هذه",
        body: "تواصل مع مالك مساحة العمل أو مع دعم {{product}}.",
      },
      notFound: {
        title: "تعذّر العثور على مساحة العمل هذه",
        body: "سجّل الخروج ثم سجّل الدخول مجدداً.",
      },
      notActive: {
        title: "مساحة العمل هذه غير متاحة",
        body: "مساحة العمل غير نشطة حالياً. تواصل مع مالك مساحة العمل أو مع دعم {{product}}.",
      },
    },
  },
  auth: {
    login: {
      heading: "تسجيل الدخول إلى {{product}}",
      title: "تسجيل الدخول",
      subtitle: "يُرجى استخدام بريد العمل الإلكتروني للمتابعة.",
      email: "بريد العمل الإلكتروني",
      password: "كلمة المرور",
      submit: "تسجيل الدخول",
      submitting: "جارٍ تسجيل الدخول…",
      createWorkspace: "إنشاء مساحة عمل",
    },
    mfa: {
      heading: "التحقق بخطوتين",
      title: "إدخال رمز التحقق",
      subtitle:
        "يُرجى فتح تطبيق المصادقة وإدخال الرمز المكوّن من {{digits}} أرقام الظاهر فيه. وإن تعذّر الوصول إلى ذلك الجهاز، فيمكن إدخال أحد رموز الاسترداد بدلاً منه.",
      codeLabel: "رمز التحقق",
      codePlaceholder: "{{example}} أو {{recoveryExample}}",
      codeHelp:
        "يتغير الرمز كل {{seconds}} ثانية، ويُستخدم رمز الاسترداد مرة واحدة فقط.",
      submit: "التحقق من الرمز",
      submitting: "جارٍ التحقق…",
      notAccepted:
        "لم يُقبل الرمز. يُرجى إدخال الرمز التالي الظاهر في التطبيق.",
      lostDevice:
        "إن فُقد الجهاز ورموز الاسترداد معاً، فيلزم أن يعيد مسؤول النظام ضبط التحقق بخطوتين للحساب. ولا يمكن ذلك من هذه الشاشة، حمايةً للحساب من أي شخص آخر.",
      backToSignIn: "تسجيل الدخول",
    },
    signup: {
      title: "إنشاء حسابك في {{product}}",
      subtitle_zero: "ابدأ إنشاء مساحة عملك، ويستغرق ذلك أقل من دقيقة.",
      subtitle_one: "ابدأ إنشاء مساحة عملك، ويستغرق ذلك نحو دقيقة واحدة.",
      subtitle_two: "ابدأ إنشاء مساحة عملك، ويستغرق ذلك نحو دقيقتين.",
      subtitle_few: "ابدأ إنشاء مساحة عملك، ويستغرق ذلك نحو {{count}} دقائق.",
      subtitle_many: "ابدأ إنشاء مساحة عملك، ويستغرق ذلك نحو {{count}} دقيقة.",
      subtitle_other: "ابدأ إنشاء مساحة عملك، ويستغرق ذلك نحو {{count}} دقيقة.",
      name: "الاسم الكامل",
      email: "بريد العمل الإلكتروني",
      emailHint: "يُتحقَّق من هذا البريد قبل تجهيز مساحة العمل.",
      password: "كلمة المرور",
      passwordHint_zero: "لا حد أدنى لعدد الأحرف.",
      passwordHint_one: "حرف واحد على الأقل.",
      passwordHint_two: "حرفان على الأقل.",
      passwordHint_few: "{{count}} أحرف على الأقل.",
      passwordHint_many: "{{count}} حرفاً على الأقل.",
      passwordHint_other: "{{count}} حرف على الأقل.",
      submit: "إنشاء الحساب",
      submitting: "جارٍ إنشاء الحساب…",
      haveAccount: "لديك حساب بالفعل؟",
      signIn: "تسجيل الدخول",
      limited:
        "أُجريت عدة محاولات تسجيل أو طلبات رمز لهذا البريد أو من شبكتك في وقت قصير. انتظر حتى ساعة ثم حاول مرة أخرى.",
    },
    verify: {
      title: "تأكيد بريدك الإلكتروني",
      subtitle:
        "أدخل الرمز المكوّن من {{digits}} أرقام الذي أُرسل إلى بريدك الإلكتروني.",
      done: "تم تأكيد البريد الإلكتروني. سجّل الدخول لإنشاء مساحة العمل.",
      code: "رمز التحقق",
      submit: "تأكيد البريد الإلكتروني",
      submitting: "جارٍ التحقق…",
      resendTitle: "لم يصلك الرمز؟",
      resendHint:
        "أدخل البريد الإلكتروني الذي سجّلت به وسنرسل إليك رمزًا جديدًا. تتوقف الرموز المرسلة قبله عن العمل.",
      resendEmail: "البريد الإلكتروني",
      resendSubmit: "إرسال رمز جديد",
      resendSending: "جارٍ إرسال رمز جديد…",
      resendRetry: "حاول إرسال رمز جديد مرة أخرى",
      resendSentBadge: "أُرسل",
      resendSent:
        "إذا كان {{email}} بانتظار التأكيد، فإن رمزًا جديدًا في طريقه إليه ولم تعد الرموز المرسلة قبله تعمل. وإذا كان لهذا البريد حساب مؤكَّد بالفعل، فقد أرسلنا إليه تذكيرًا بتسجيل الدخول بدلًا من ذلك.",
      resendLimited:
        "طلبتَ عدة رموز في وقت قصير. انتظر حتى ساعة ثم اطلب مرة أخرى. يبقى أحدث رمز وصلك صالحًا حتى تنتهي صلاحيته.",
    },
    workspace: {
      title: "إنشاء مساحة العمل",
      subtitle:
        "اختر الدولة التي تعمل فيها شركتك، فيثبّت {{product}} حزمة تلك الدولة ويحتفظ بالعملة والمنطقة الزمنية واللغة التي تختارها.",
      name: "اسم الشركة",
      code: "كود مساحة العمل",
      codeHint: "أحرف إنجليزية صغيرة وأرقام وشرطات، ويُستخدم في الروابط.",
      country: "الدولة",
      countryHint: "تُثبَّت حزمة هذه الدولة في مساحة عملك.",
      countriesLoading: "جارٍ تحميل الدول المتاحة للاختيار…",
      countriesFailed: "تعذّر تحميل قائمة الدول.",
      noCountries:
        "لا توجد دولة متاحة للاختيار بعد، لذا لا يمكن إنشاء مساحة عمل. تواصل مع الدعم.",
      chooseCountry: "اختر دولة",
      currency: "العملة",
      currencyHint: "العملة التي تُمسَك بها دفاترك.",
      chooseCurrency: "اختر عملة",
      timezone: "المنطقة الزمنية",
      timezoneHint:
        "تُعرض التواريخ والأوقات في مساحة العمل بهذه المنطقة، ما لم يختر الشخص منطقته.",
      chooseTimezone: "اختر منطقة زمنية",
      language: "اللغة",
      languageHint:
        "لغة مساحة العمل ومنطقتها، ويظل كل شخص يختار لغة شاشاته بنفسه.",
      chooseLanguage: "اختر لغة",
      submit: "إنشاء مساحة العمل",
      submitting: "جارٍ إنشاء مساحة العمل…",
      errors: {
        name: "أدخل اسم الشركة، من {{min}} إلى {{max}} حرف.",
        code: "أدخل من {{min}} إلى {{max}} من الأحرف الإنجليزية الصغيرة أو الأرقام أو الشرطات.",
        codeTaken: "هذا الكود تستخدمه مساحة عمل أخرى. اختر كوداً مختلفاً.",
        country: "اختر الدولة التي تعمل فيها شركتك.",
        currency: "اختر عملة.",
        timezone: "اختر منطقة زمنية.",
        language: "اختر لغة.",
      },
    },
  },
  errors: {
    AUTHENTICATION_FAILED: {
      credentials:
        "البريد الإلكتروني أو كلمة المرور غير صحيحة، أو لا يمكن للحساب تسجيل الدخول بعد. تحقّق من البريد الإلكتروني وكلمة المرور. وإن كنت قد أنشأت الحساب للتو، فأكّد بريدك الإلكتروني أولاً. وبعد {{attempts}} محاولات خاطئة يُقفل الحساب {{minutes}} دقيقة، فانتظر ثم أعد المحاولة. ولا تتوفر بعد في {{product}} إعادة تعيين كلمة المرور، فتواصل مع الدعم إن نسيتها.",
      noFactor:
        "يتطلب حسابك التحقق بخطوتين، لكن لم يُربط به أي تطبيق مصادقة. تواصل مع الدعم لإعادة ضبط التحقق بخطوتين في حسابك.",
      codeInvalid:
        "الرمز لا يطابق تطبيق المصادقة ولا أي رمز استرداد لم يُستخدم. أدخل الرمز الظاهر الآن في تطبيق المصادقة، أو أحد رموز الاسترداد التي لم تستخدمها بعد.",
      codeUsed:
        "يُستخدم كل رمز مرة واحدة فقط. انتظر حتى يعرض تطبيق المصادقة الرمز التالي ثم أدخله.",
      attemptExpired:
        "مرّ وقت طويل بين إدخال كلمة المرور والرمز، إذ يجب إتمام الخطوة الثانية خلال {{minutes}} دقائق. ارجع إلى صفحة تسجيل الدخول وابدأ من جديد.",
    },
    VALIDATION_FAILED: {
      email:
        "البريد الإلكتروني فارغ أو غير مكتمل. أدخل بريدك الإلكتروني كاملاً، مثل name@company.com.",
      passwordTooShort:
        "كلمة المرور أقل من {{min}} حرفاً. استخدم {{min}} حرفاً على الأقل، وتسهل الجملة القصيرة تذكّرها.",
      passwordTooLong:
        "كلمة المرور أطول من {{max}} حرفاً. استخدم كلمة مرور أقصر.",
      passwordSpace:
        "تبدأ كلمة المرور أو تنتهي بمسافة. احذف المسافة من أولها أو آخرها.",
      codeFormat:
        "الرمز ليس من {{digits}} أرقام. أدخل الرمز المكوّن من {{digits}} أرقام الوارد في رسالة التحقق.",
      codeInvalid:
        "الرمز غير صحيح، أو استُخدم من قبل، أو مضى عليه أكثر من {{minutes}} دقيقة. راجع الرمز وأدخله مرة أخرى. ولا يستطيع {{product}} بعد إرسال رمز جديد، ولا تؤدي إعادة التسجيل إلى إرساله، فإن انتهت صلاحيته فتواصل مع الدعم.",
    },
    RATE_LIMITED:
      "صدرت من شبكتك محاولات كثيرة لتسجيل الدخول أو لإدخال رمز التحقق: أكثر من {{limit}} محاولة خلال {{minutes}} دقائق. انتظر بضع دقائق ثم أعد المحاولة.",
    NETWORK_ERROR:
      "تعذّر الوصول إلى {{product}}، فقد يكون الاتصال منقطعاً أو يُعاد تشغيل {{product}}. تحقّق من الاتصال وانتظر قليلاً ثم أعد المحاولة.",
    UNKNOWN:
      "ردّ {{product}} بخطأ دون أن يبيّن نوعه. أعد المحاولة، وإن تكرر ذلك فتواصل مع الدعم واذكر المرجع.",
    fallback:
      "لم يكتمل هذا الإجراء. أعد المحاولة، وإن تكرر ذلك فتواصل مع الدعم.",
    TOKEN_EXPIRED:
      "احتاجت جلستك إلى التجديد، ولم يتمكن {{product}} من تجديدها. أعد تحميل الصفحة، وإن طلب {{product}} تسجيل الدخول فسجّل الدخول مجدداً.",
    TOKEN_INVALID:
      "تعذّر على {{product}} التعرّف على جلستك، كأن يكون {{product}} قد غيّر مفاتيح الأمان. أعد تحميل الصفحة، وإن طلب {{product}} تسجيل الدخول فسجّل الدخول مجدداً.",
    NO_SESSION:
      "لا يحتفظ هذا المتصفح بجلسة يستطيع {{product}} تجديدها، فإما أنك سجّلت الخروج، أو حذف المتصفح ما يحفظه من بيانات {{product}}، أو بلغت الجلسة حدها الزمني: {{idleHours}} ساعة دون استخدام، أو {{days}} أيام منذ تسجيل الدخول. سجّل الدخول.",
    SESSION_EXPIRED:
      "بلغت جلستك حدها الزمني: {{idleHours}} ساعة دون استخدام، أو {{days}} أيام منذ تسجيل الدخول. سجّل الدخول مجدداً، وستحتاج إلى إعادة إدخال ما لم يُحفظ.",
    SESSION_REVOKED:
      "انتهت هذه الجلسة، فقد سُجّل الخروج منها، أو أُنهيت عند تغيير كلمة المرور، أو بلغت حدها الزمني. سجّل الدخول مجدداً، وستحتاج إلى إعادة إدخال ما لم يُحفظ.",
    NOT_A_USER_SESSION:
      "تسجيل الخروج خاص بالأشخاص، ولا ينطبق على تطبيقات الربط. ألغِ تطبيق الربط لإيقاف وصوله.",
    UNSUPPORTED_MEDIA_TYPE:
      "لم يُرسَل الطلب بالصيغة التي يتوقعها {{product}}، فلم يُنفّذه. والخلل في الشاشة أو في تطبيق الربط، لا فيما فعلته. أعد تحميل الصفحة وحاول مجدداً، وإن تكرر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    SIGNING_KEYS_UNAVAILABLE:
      "لا يستطيع {{product}} حالياً تسجيل دخول أي شخص ولا التحقق من أي جلسة، لأن مفاتيح الأمان غير مضبوطة على الخادم كما ينبغي. والخلل من جانبنا، لا فيما فعلته. انتظر بضع دقائق ثم أعد المحاولة، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    VERIFICATION_PEPPER_UNAVAILABLE:
      "لا يستطيع {{product}} حالياً إنشاء رموز التحقق ولا التحقق منها، لأن أحد الإعدادات غير موجود على الخادم، فلم يكتمل إنشاء حسابك أو تأكيد بريدك. والخلل من جانبنا. انتظر بضع دقائق ثم أعد المحاولة، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    IDENTITY_ROW_UNREACHABLE:
      "تعذّر على {{product}} إرسال رمز جديد لهذا البريد بسبب خلل من جانبنا، فلم يُرسل شيء وبقيت الرموز التي لديك كما هي. انتظر بضع دقائق ثم أعد المحاولة، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    INVITATION_NOT_RECORDED:
      "تعذّر على {{product}} تسجيل الدعوة بسبب خلل من جانبنا، فلم تُرسل أي دعوة ولم يُرسل أي بريد. انتظر بضع دقائق ثم أعد المحاولة، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    REFRESH_TOKEN_USED:
      "توقف {{product}} أثناء تجديد جلستك، لأن أحد فحوص الأمان في قاعدة البيانات رفض التغيير. والخلل في {{product}}، لا فيما فعلته. سجّل الدخول مجدداً، وإن تكرر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    REFRESH_TOKEN_UPDATE_MUST_USE:
      "توقف {{product}} أثناء تجديد جلستك، لأن أحد فحوص الأمان في قاعدة البيانات رفض التغيير. والخلل في {{product}}، لا فيما فعلته. سجّل الدخول مجدداً، وإن تكرر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    REFRESH_TOKEN_REWRITE:
      "توقف {{product}} أثناء تجديد جلستك، لأن أحد فحوص الأمان في قاعدة البيانات رفض التغيير. والخلل في {{product}}، لا فيما فعلته. سجّل الدخول مجدداً، وإن تكرر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    SESSION_OWNER_MISMATCH:
      "لم تُحفظ كلمة المرور الجديدة، لأن أحد فحوص الأمان رفض إنهاء جلسات تخص شخصاً آخر. والخلل في {{product}}، لا فيما فعلته. أعد محاولة التغيير، وإن تكرر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    RATE_LIMIT_UNAVAILABLE:
      "لا يستطيع {{product}} حالياً إحصاء طلبات تسجيل الدخول، فلا يقبل أياً منها في الوقت الحالي. والخلل من جانبنا. انتظر دقيقة ثم أعد المحاولة، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    CONFIGURATION_INVALID:
      "تعذّر الوصول إلى {{product}} لأن خادمه لم يبدأ العمل. انتظر بضع دقائق ثم أعد المحاولة، وإن استمر ذلك فتواصل مع الدعم.",
    FILE_TOO_LARGE:
      "حجم الملف أكبر من حد الرفع المسموح في هذا التثبيت، وهو {{limit}}. ارفع ملفاً أصغر، أو اضغطه أو قسّمه أولاً.",
    MALWARE_DETECTED:
      "عثر فحص الفيروسات على محتوى ضار في هذا الملف، فلم يحفظه {{product}}. لا تفتح الملف، وافحص جهازك، واطلب من مُرسِله نسخة سليمة ثم ارفعها. وإن كنت متأكداً من سلامة الملف فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    ANTIVIRUS_UNAVAILABLE:
      "لم يستجب فاحص الفيروسات، فلم يُحفظ ملفك، إذ لا يحفظ {{product}} أي ملف تعذّر فحصه. انتظر بضع دقائق ثم ارفع الملف مرة أخرى، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    STORAGE_UNAVAILABLE: {
      uploading:
        "لم يقبل مخزن الملفات الملف، فلم يُحفظ. انتظر بضع دقائق ثم ارفع الملف مرة أخرى، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
      opening:
        "تعذّر على {{product}} إنشاء رابط لتنزيل الملف، والملف نفسه ما زال محفوظاً. انتظر بضع دقائق ثم افتح الملف مرة أخرى، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    },
    FEATURE_NOT_CONFIGURED: {
      twoStepSignIn:
        "لم يُجهَّز التحقق بخطوتين على هذا التثبيت من {{product}}، فلا يمكن تفعيله. تواصل مع الدعم، إذ يلزم أن يضبطه المشغّل (تذكر التفاصيل اسم الإعداد الناقص).",
      uploading:
        "لم يُجهَّز رفع الملفات على هذا التثبيت من {{product}}، إذ ينقصه تخزين الملفات أو فحص الفيروسات، فلا يُقبل أي ملف. تواصل مع الدعم، إذ يلزم أن يضبطه المشغّل (تذكر التفاصيل أسماء الإعدادات الناقصة).",
    },
    DATE_OUTSIDE_FISCAL_CALENDAR:
      "التاريخ خارج كل السنوات المالية لهذه الشركة، فلم يُحفظ شيء. اختر تاريخاً داخل إحدى سنواتها المالية، أو اطلب من المدير المالي فتح تلك السنة أولاً.",
    FISCAL_YEAR_NOT_FOUND:
      "فترة هذا القيد لا تنتمي إلى سنة مالية لشركة القيد، فتعذّر على Atlas منحه رقم قيد ولم يُرحَّل شيء. تحقّق من تاريخ القيد وشركته، واطلب من المدير المالي فتح السنة المالية لتلك الشركة ثم تسجيل القيد في فترة داخلها. وإن كانت السنة مفتوحة بالفعل فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    GRANT_NOT_ALLOWED: {
      self: "لا يجوز لأحد أن يمنح نفسه دوراً، أو يضيف صلاحية إلى دور يحمله، أو يغيّر إسناد دوره إلا بإنهائه الآن. اطلب من مالك مساحة العمل، أو من مسؤول آخر، إجراء التغيير.",
      owner:
        "لا يمنح دور المالك أو يغيّره أو ينهيه، ولا يوقف مالكاً أو يزيله، إلا مالك مساحة العمل. اطلب من مالك مساحة العمل إجراء التغيير.",
      allAccess:
        "لا يمنح الوصول الكامل أو يغيّره أو يسحبه إلا مالك مساحة العمل: إضافته إلى دور أو إزالته أو حذف دور يحمله أو منح دور يحمله أو تغييره أو إنهاؤه. اطلب ذلك من مالك مساحة العمل، ولمنح أحد وصولاً واسعاً اختر الصلاحيات التي يحتاجها بدلاً من الوصول الكامل.",
      lastOwner:
        "سيترك هذا مساحة العمل بلا مالك نشط. امنح شخصاً آخر دور المالك أولاً بنطاق كامل ودون تاريخ انتهاء، ثم أجرِ هذا التغيير.",
    },
    SYSTEM_ROLE_PROTECTED:
      "يحمي {{product}} الأدوار المدمجة: لا يمكن حذفها، ولا يمكن أن يفقد دور المالك الوصول الكامل. إذا كانت الملكية تتغيّر فامنح شخصاً آخر دور المالك أولاً بنطاق كامل ودون تاريخ انتهاء، وإلا فانزع الأشخاص من الدور بدلاً من حذفه.",
    SELF_APPROVAL_BLOCKED:
      "لا يمكن اعتماد مطالبة مصاريف من الموظف صاحب المطالبة أو من الشخص الذي أنشأها، حتى لو كان يستطيع اعتماد مطالبات الآخرين. اطلب من معتمد آخر مراجعة هذه المطالبة.",
    TENANT_CONTEXT_REQUIRED:
      "تعذّر على {{product}} إكمال تجهيز مساحة العمل هذه، لأن أحد فحوص الأمان في قاعدة البيانات رفض إنشاء أدوارها خارج مساحة العمل نفسها. والخلل في {{product}}، لا فيما فعلته. أعد المحاولة بعد بضع دقائق، وإن استمر ذلك فتواصل مع الدعم واذكر المرجع {{requestId}}.",
    EMPLOYEE_NOT_LINKED:
      "حسابك غير مرتبط بسجل موظف، فلا يوجد لدى {{product}} موظف تُسجَّل له مطالبة المصاريف هذه. اطلب من الموارد البشرية ربط حسابك بسجل موظفك، ثم أعد تقديم المطالبة.",
    STATUS_REQUIRES_ACTION:
      "لا يمكن ضبط هذه الحالة بتعديل السجل، لأن لها أثراً محاسبياً أو تعني اعتماداً. استخدم الإجراء الخاص بالسجل بدلاً من ذلك، مثل «ترحيل» لمسير الرواتب. وإن لم يوجد إجراء كهذا فلا يمكن ضبط الحالة بعد. لم يُحفظ أي تغيير.",
    MFA_ALREADY_ENROLLED:
      "التحقق بخطوتين مُعَدّ بالفعل في هذا الحساب، وما زال تطبيق المصادقة الحالي ورموز الاسترداد تعمل. ولتغيير الهاتف، أوقف التحقق بخطوتين برمز حالي أو برمز استرداد، ثم أعدّه من جديد.",
    MFA_REQUIRED:
      "يتطلب دورك في مساحة العمل هذه، أو قاعدة مساحة العمل نفسها، التحقق بخطوتين، ولم يُعَدّ لحسابك بعد. أعدّه الآن بتطبيق مصادقة، فتُفتح مساحة العمل فور تأكيده. وإن كنت قد سجّلت الدخول من قبل، فسجّل الدخول مرة أخرى لإعداده.",
    MFA_RESET_OWNER_REQUIRED:
      "لا يستطيع إعادة تعيين التحقق بخطوتين لعضو آخر إلا أحد مالكي مساحة العمل هذه. اطلب ذلك من أحد المالكين، وإن لم يكن في مساحة العمل مالك آخر فتواصل مع دعم {{product}}. لم يتغير شيء.",
    MFA_RESET_OWN_FACTOR:
      "لا يمكنك إعادة تعيين التحقق بخطوتين لحسابك من هنا. أوقفه من إعدادات الأمان الخاصة بك برمز حالي أو برمز استرداد، وإن لم يتوفر أي منهما فاطلب ذلك من مالك آخر أو من دعم {{product}}. لم يتغير شيء.",
    MFA_RESET_NEEDS_SUPPORT:
      "ينتمي هذا الشخص أيضاً إلى مساحة عمل لا تملكها، فلا يمكنك إعادة تعيين التحقق بخطوتين لحسابه. يستطيع دعم {{product}} إعادة تعيينه بعد التحقق من هويته. لم يتغير شيء.",
    MFA_NOT_SET_UP:
      "لا يوجد لدى هذا الشخص تحقق بخطوتين لإعادة تعيينه. لم يتغير شيء ولم تُرسَل أي رسالة.",
    REAUTHENTICATION_REQUIRED:
      "أدخل كلمة المرور الحالية لإعداد التحقق بخطوتين. يطلبها {{product}} حتى لا يتمكّن من يستخدم جلسة تركتها مفتوحة من تفعيله بهاتفه هو. إذا طُلبت منك مرة أخرى، فقد استغرق الإعداد أكثر من 10 دقائق أو بدأ من مكان آخر: أدخل كلمة المرور وابدأ من جديد. لم يتغيّر شيء بعد.",
    ACCOUNT_ROLE_NOT_MAPPED:
      "لم يجد {{product}} حساباً صالحاً للترحيل إليه لأحد أغراضه في هذا الكيان القانوني، ولا حتى الحساب المعلّق. على المسؤول إضافة الحساب أو إعادة تفعيله في دليل الحسابات، إذ لا توجد بعد شاشة لتعديل التوجيه المحاسبي. لم يُرحَّل شيء.",
    ACCOUNT_ROLE_WRONG_TYPE:
      "أحد أغراض الرواتب موجَّه إلى نوع خاطئ من الحسابات، مثل توجيه صافي الأجور إلى حساب مصروفات. وجّهه إلى حساب من النوع الصحيح ثم أعد الترحيل. لم يُرحَّل شيء.",
    PAY_RUN_EMPTY:
      "لا يوجد في مسير الرواتب هذا ما يُرحَّل: فهو لا يذكر أي موظف، أو أن جميع مبالغه صفر. أدخل المبالغ ثم أعد الترحيل. لم يُرحَّل شيء.",
    DEDUCTIONS_MISMATCH:
      "لا يتطابق إجمالي الاستقطاعات مع بنود الاستقطاعات لموظف واحد على الأقل. أرسل أحدهما فقط أو اجعلهما متطابقين ثم أعد الترحيل. لم يُرحَّل شيء.",
    EMPLOYEE_NOT_FOUND:
      "يذكر مسير الرواتب موظفاً غير موجود في مساحة العمل هذه. راجع موظفي المسير ثم أعد الترحيل. لم يُرحَّل شيء.",
    EMPLOYEE_DEPARTMENT_NOT_FOUND:
      "أحد موظفي مسير الرواتب تابع لقسم لم يعد موجوداً. صحّح قسم الموظف ثم أعد الترحيل. لم يُرحَّل شيء.",
    EMPLOYEE_COMPANY_MISMATCH:
      "يضم مسير الرواتب موظفين تابعين لشركة أخرى، والمسير الواحد يخص رواتب شركة واحدة. قسّم المسير أو صحّح أقسام الموظفين. لم يُرحَّل شيء.",
    PAY_RUN_COMPANY_REQUIRED:
      "لا يحدد مسير الرواتب الشركة التي يخصها، ولا يستطيع {{product}} استنتاجها من موظفيه. أنشئ المسير مع شركته ثم رحّله. لم يُرحَّل شيء.",
    NET_PAY_NEGATIVE:
      "الاستقطاعات أكبر من إجمالي الأجر لموظف واحد على الأقل، فيصبح صافي الأجر سالباً. خفّض الاستقطاعات، بما فيها استرداد السلف، ثم أعد الترحيل. لم يُرحَّل شيء.",
    INVENTORY_ROLE_CONFLICT:
      "دور المخزون لهذا الصنف، الذي ضبطه شخص أو ثبّته مخزون مُسجَّل، حسابُ مخزون يخالفه الإنتاج، إذ سيستخدم الإنتاج الحساب الآخر. استخدم صنفاً آخر، أو اضبط دور المخزون للصنف على الدور الذي يستخدمه الإنتاج قبل تسجيل أي مخزون، ثم أعد المحاولة.",
    STOCK_OWNERSHIP_VIOLATION:
      "حركة المخزون تخالف ملكية المخزون: فهي تعطي بضاعة العميل قيمة، أو تعيد تقييم مخزون لا يحتفظ به المستودع. انقل بضاعة العميل بقيمة صفر، أو أعد تقييم المخزون الذي يحتفظ به المستودع فقط، ثم أعد المحاولة.",
    LEGAL_ENTITY_REQUIRED:
      "تمسك مساحة العمل هذه دفاترها بأكثر من عملة واحدة، لذا يعرض التقرير دفاتر كيان قانوني واحد في كل مرة، بعملة ذلك الكيان. اختر الكيان القانوني. لم يُجمع أي مبلغ عبر العملات.",
    ALLOCATION_PARTY_MISMATCH:
      "هذه الفاتورة تخص عميلاً أو مورداً آخر، فلا يمكن أن تسددها هذه الدفعة. اختر فاتورة تخص الطرف الذي دفع أو قبض الدفعة. لم يُحفظ أي تغيير.",
    ALLOCATION_DIRECTION_MISMATCH:
      "المقبوض من العميل يسدد فواتير المبيعات، والدفعة للمورد تسدد فواتير الموردين. اختر مستنداً من النوع الصحيح. لم يُحفظ أي تغيير.",
    ALLOCATION_LEGAL_ENTITY_MISMATCH:
      "الدفعة والفاتورة مسجلتان في دفاتر كيانين قانونيين مختلفين. خصّص الدفعة لفاتورة في دفاتر كيانها القانوني نفسه. لم يُحفظ أي تغيير.",
    CROSS_CURRENCY_ALLOCATION_NOT_SUPPORTED:
      "الدفعة والفاتورة بعملتين مختلفتين، ولا يستطيع {{product}} بعد تسوية عملة بأخرى. اختر فاتورة بعملة الدفعة. لم يُحفظ أي تغيير.",
    ALLOCATION_EXCEEDS_AMOUNT_DUE:
      "هذا المبلغ أكبر مما تبقى مستحقاً على الفاتورة. لا تخصّص أكثر من المبلغ المستحق، ويبقى باقي الدفعة على الحساب لفاتورة أخرى. لم يُحفظ أي تغيير.",
    ALLOCATION_EXCEEDS_UNALLOCATED:
      "مجموع هذه المبالغ أكبر مما تبقى من الدفعة دون تخصيص. خفّضها، أو استخدم دفعة أخرى للباقي. لم يُحفظ أي تغيير.",
    ALLOCATION_DATE_BEFORE_DOCUMENT:
      "التاريخ يسبق تاريخ الدفعة أو تاريخ الفاتورة. اختر تاريخاً لاحقاً، أو اتركه فارغاً لاستخدام أحدث التاريخين. لم يُحفظ أي تغيير.",
    AMOUNT_PRECISION_EXCEEDED:
      "المبلغ يحتوي على خانات عشرية أكثر مما تسمح به العملة، ولا يقرّبه {{product}} نيابة عنك. أدخله مرة أخرى بخانات عشرية أقل.",
    INVOICE_WRITTEN_OFF:
      "شُطبت هذه الفاتورة، فلا يمكن تخصيص دفعة لها. سجّل المبلغ المسترد عليها كاسترداد بدلاً من ذلك.",
    ACCOUNT_ROLE_TYPE_MISMATCH:
      "الحساب المختار لهذا الغرض من نوع خاطئ، مثل حساب أصول لمبالغ دفعها العملاء مقدماً. اختر حساباً من النوع الذي يحتاجه الغرض.",
    SETTLE_THROUGH_PAYMENT:
      "تُسدَّد فاتورة المورد بتسجيل دفعة للمورد وتخصيصها للفاتورة، لا بتعليمها مدفوعة. لم يتغير أي شيء.",
    IDENTITY_SELF_ONLY:
      "البريد الإلكتروني الذي يسجّل به الشخص دخوله واسمه يتبعان حسابه الخاص في {{product}}، وهو حساب مشترك بين كل مساحات العمل التي ينتمي إليها، فلا يغيّرهما إلا هو من ملفه الشخصي. لم يتغير أي شيء.",
    EMAIL_ADDRESS_IN_USE:
      "يوجد حساب آخر في {{product}} يسجّل الدخول بهذا البريد الإلكتروني. لم يتغير أي شيء، وما زال بريدك الحالي يعمل. اختر عنواناً آخر.",
    PLATFORM_OPERATOR_REQUIRED:
      "هذا الإجراء محصور في مشغّلي منصة {{product}}، ولا يستطيع أي دور في مساحة العمل تنفيذه، حتى صلاحية الوصول الكامل. لإضافة شخص إلى مساحة عملك، اطلب منه التسجيل ببريده الإلكتروني ثم ادعه. لم يتغير أي شيء.",
    DERIVED_AMOUNT_MISMATCH:
      "قيمة {{field}} التي أدخلتها، {{received}}، لا تساوي {{expected}} التي يحسبها {{product}} من السطور وأكواد ضريبتها. اترك الحقل فارغاً ليُملأ تلقائياً، أو صحّح السطور حتى تعطي القيمة التي تتوقعها.",
    TAX_CODE_NOT_APPLICABLE: {
      NotEffective:
        "كود الضريبة في السطر {{line}} غير سارٍ في تاريخ المستند ({{date}}). اختر إصدار الكود الساري في ذلك التاريخ، أو اترك الكود فارغاً لاستخدام الكود الافتراضي.",
      OtherEntity:
        "كود الضريبة في السطر {{line}} يخص كياناً قانونياً لشركة أخرى. اختر كود ضريبة يخص شركة هذا المستند.",
      Inactive: "كود الضريبة في السطر {{line}} موقوف. اختر كود ضريبة نشطاً.",
      NotSalesTax:
        "الكود في السطر {{line}} ضريبة خصم أو ضريبة أخرى، وليس ضريبة قيمة مضافة أو مبيعات. اختر للسطر كود ضريبة قيمة مضافة أو ضريبة مبيعات.",
      NotFound:
        "يشير السطر {{line}} إلى كود ضريبة غير موجود في مساحة العمل. اختر أحد أكواد الضريبة في مساحة العمل.",
    },
    STATED_TAX_OUT_OF_TOLERANCE:
      "ضريبة المورد في السطر {{line}}، وقدرها {{received}}، لا تطابق {{taxCode}} ({{ratePercent}}%) الذي يعطي {{expected}}. راجع فاتورة المورد؛ فإن كان قد احتسب سعراً آخر فاختر كود ذلك السعر للسطر، وإن لم يحتسب ضريبة فاجعل السطر «بلا ضريبة».",
    TAX_RECOMPUTE_MISMATCH:
      "تغيّر كود ضريبة في هذا المستند بعد حفظه، فلم تعد أرقامه هي التي سيرحّلها {{product}}، ولم يُرحَّل شيء. افتح المستند واحفظه مرة أخرى ليأخذ الأرقام الحالية، ثم رحّله. وإذا تغيّرت مبالغ فاتورة معتمدة عادت إلى مسودة لتُعتمد من جديد.",
    TAX_CODE_IN_USE:
      "تستخدم مستندات مُرحَّلة هذا الإصدار من كود الضريبة، فلا يمكن تغيير {{field}}. لتغيير السعر ابتداءً من تاريخ، أضف إصداراً جديداً من الكود بتاريخ بدء لاحق، وحدّد لهذا الإصدار تاريخ انتهاء.",
    DOCUMENT_HAS_NO_LINES:
      "لا سطور في هذا المستند، فلا شيء يُرحَّل. أضف السطور التي يفوترها، ثم رحّله.",
    UNKNOWN_CURRENCY:
      "{{currencyCode}} ليست عملة يمكن لـ {{product}} حفظ المبالغ بها. اختر العملة التي صدر بها المستند فعلاً.",
    DOCUMENT_TOTALS_MISMATCH:
      "أوقف {{product}} الترحيل لأن مجاميع المستند لا تتطابق، ولم يُرحَّل شيء. تواصل مع الدعم واذكر المرجع {{requestId}}.",
    COSTING_METHOD_LOCKED:
      "تثبّتت طريقة تكلفة هذا الصنف عند أول حركة لمخزونه، ولا يمكن تغييرها: فالمخزون الذي قُيّم بطريقة لا يُعاد تقييمه بطريقة أخرى. أبقِ الطريقة، أو أنشئ صنفاً جديداً للطريقة الأخرى.",
    COSTING_METHOD_NOT_ALLOWED:
      "طريقة التكلفة هذه ليست من الطرق التي تسمح بها حزمة بلد الكيان القانوني. اختر طريقة من قائمة الحزمة الظاهرة في إعدادات التكلفة، ثم أعد المحاولة.",
    NUMBER_SERIES_PATTERN_INVALID:
      "لا يصلح هذا النمط لترقيم المستندات ({{reason}}). ابدأ بحرف، واستخدم الحروف اللاتينية والأرقام و - / _ . فقط، وأضف عدّاداً واحداً مثل {######}، واستخدم {FY} فقط لسلسلة تبدأ من جديد كل سنة مالية.",
    NUMBER_SERIES_IN_USE:
      "أصدرت سلسلة الترقيم هذه أرقاماً، فلا يمكن تغيير إعادة ضبطها. أبقِ إعادة الضبط، وغيّر النمط إن أردت شكلاً آخر للأرقام.",
    NUMBER_SERIES_PATTERN_IN_USE:
      "تتداخل بادئة النمط مع سلسلة {{documentKind}}، فقد تتشابه أرقامهما. اختر بادئة لا تبدأ بها بادئة سلسلة أخرى ولا تبدأ هي بها.",
    NUMBER_SERIES_WOULD_COLLIDE:
      "بهذا النمط ستُصدر السلسلة الرقم {{example}} مرة أخرى، وهو مستخدم بالفعل. اختر بادئة أو طول عدّاد آخر حتى لا يتكرر رقم صادر.",
    SCOPE_DENIED: {
      default:
        "صلاحيتك لا تشمل هذا. اطلب من مسؤول النظام دوراً يغطي نطاقه ذلك.",
      LINES_CARRY_NO_BRANCH:
        "صلاحيتك على دفاتر هذا الكيان القانوني مقصورة على بعض الفروع، وقيود دفتر الأستاذ التي يجمعها هذا التقرير لا تسجل كلها فرعاً بعد. والرقم الخاص بفروعك سيُسقط مبيعاتها وتكاليفها، لذا لا يُعرض أي رقم. يستطيع من له صلاحية على كل شركات الكيان القانوني تشغيل هذا التقرير.",
      LINES_CARRY_NO_COMPANY:
        "شركتك تشترك في كيانها القانوني مع شركات أخرى، وقيود دفتر الأستاذ التي يجمعها هذا التقرير لا تسجل كلها شركة أو فرعاً، فلا يمكن تمييز قيود شركتك من قيود غيرها، لذا لا يُعرض أي رقم. يستطيع من له صلاحية على كل شركات الكيان القانوني تشغيل هذا التقرير.",
      BRANCH_LINES_DO_NOT_BALANCE:
        "الجزء الذي تصل إليه من دفاتر هذا الكيان القانوني لا يتوازن وحده لأن قيداً ينقل مبلغاً بينه وبين باقي الدفاتر. لذا لا يُعرض له ميزان مراجعة ولا ميزانية عمومية، ويمكن عرض أرباحه وخسائره.",
      SCOPE_KIND_NOT_IN_LEDGER:
        "صلاحيتك مقصورة على إدارة أو على سجلاتك أنت، ودفتر الأستاذ لا يسجل أياً منهما، لذا لا يُعرض أي رقم. يستطيع من له صلاحية على فرع أو شركة أو على مساحة العمل كلها تشغيل هذا التقرير.",
      LEGAL_ENTITY_NOT_IN_SCOPE:
        "صلاحيتك لا تشمل أي فرع ولا أي شركة من الكيان القانوني الذي طُلب له هذا التقرير، فلا شيء في دفاتره يخصك، ولا يُعرض أي رقم. اختر كياناً قانونياً تشمله صلاحيتك، أو اطلب من شخص له صلاحية عليه تشغيل التقرير.",
    },
  },
  setup: {
    center: {
      title: "مركز الإعداد",
      subtitle:
        "تجهيز موجَّه لمساحة العمل من البداية حتى الاستعداد للتشغيل الفعلي.",
      continue: "متابعة الإعداد",
      score: "درجة الجاهزية: {{score}}",
      blockingErrors_zero: "لا توجد أخطاء مانعة",
      blockingErrors_one: "خطأ مانع واحد",
      blockingErrors_two: "خطآن مانعان",
      blockingErrors_few: "{{count}} أخطاء مانعة",
      blockingErrors_many: "{{count}} خطأً مانعاً",
      blockingErrors_other: "{{count}} خطأ مانع",
      warnings_zero: "لا توجد تنبيهات",
      warnings_one: "تنبيه واحد",
      warnings_two: "تنبيهان",
      warnings_few: "{{count}} تنبيهات",
      warnings_many: "{{count}} تنبيهاً",
      warnings_other: "{{count}} تنبيه",
      categories: "مجالات الإعداد",
      blockingItems: "البنود المانعة",
      auditLog: "سجل المراجعة",
      noBlockingItems: "لا توجد بنود مانعة.",
    },
    phases: {
      TaxConfiguration: "إعداد الضرائب",
      ApprovalWorkflows: "مسارات الاعتماد",
      OpeningBalances: "الأرصدة الافتتاحية",
    },
    messages: {
      noTaxCodes: "لا يوجد كود ضريبة نشط في مساحة العمل.",
      noApprovalMatrix:
        "لا توجد مصفوفة اعتمادات نشطة، فلا يمكن اعتماد المستندات.",
      noPostableAccounts: "لا يحتوي دليل الحسابات على حسابات فرعية.",
      openingBalancesNeedAccounts:
        "لا يمكن إدخال الأرصدة الافتتاحية قبل إنشاء الحسابات.",
    },
  },
  home: {
    title: "الرئيسية",
    readiness: "درجة الجاهزية {{score}}",
    blockingItems_zero: "لا توجد بنود مانعة",
    blockingItems_one: "بند مانع واحد",
    blockingItems_two: "بندان مانعان",
    blockingItems_few: "{{count}} بنود مانعة",
    blockingItems_many: "{{count}} بنداً مانعاً",
    blockingItems_other: "{{count}} بند مانع",
    newSalesOrder: "إنشاء أمر بيع",
    continueSetup: "متابعة الإعداد",
    pendingApprovals: "طلبات بانتظار الاعتماد",
    recentRecords: "أحدث المستندات",
    columns: {
      type: "النوع",
      record: "السجل",
      submittedBy: "مقدّم الطلب",
      date: "التاريخ",
      number: "الرقم",
      status: "الحالة",
      total: "الإجمالي",
    },
    kpis: {
      revenue: "الإيرادات (آخر {{days}} يوماً)",
      revenueMeta: "فواتير مُرحَّلة ومُرسَلة للعميل ومُسدَّدة",
      receivables: "المستحق على العملاء",
      nothingOverdue: "لا توجد فواتير متأخرة السداد",
      overdue_zero: "لا توجد فواتير متأخرة السداد",
      overdue_one: "فاتورة واحدة متأخرة السداد",
      overdue_two: "فاتورتان متأخرتا السداد",
      overdue_few: "{{count}} فواتير متأخرة السداد",
      overdue_many: "{{count}} فاتورة متأخرة السداد",
      overdue_other: "{{count}} فاتورة متأخرة السداد",
      commitments: "التزامات الشراء",
      commitmentsMeta: "أوامر شراء معتمدة أو قيد التنفيذ",
      openSalesOrders: "أوامر البيع المفتوحة",
      openSalesOrdersMeta: "لم تصدر فواتيرها ولم تُلغَ",
    },
    alerts: {
      provisioning: "ما زالت مساحة العمل قيد التجهيز.",
      overdueInvoices_zero: "لا توجد فواتير متأخرة السداد.",
      overdueInvoices_one: "فاتورة واحدة متأخرة السداد.",
      overdueInvoices_two: "فاتورتان متأخرتا السداد.",
      overdueInvoices_few: "{{count}} فواتير متأخرة السداد.",
      overdueInvoices_many: "{{count}} فاتورة متأخرة السداد.",
      overdueInvoices_other: "{{count}} فاتورة متأخرة السداد.",
      approvalsPending_zero: "لا توجد طلبات بانتظار الاعتماد.",
      approvalsPending_one: "طلب واحد بانتظار الاعتماد.",
      approvalsPending_two: "طلبان بانتظار الاعتماد.",
      approvalsPending_few: "{{count}} طلبات بانتظار الاعتماد.",
      approvalsPending_many: "{{count}} طلباً بانتظار الاعتماد.",
      approvalsPending_other: "{{count}} طلب بانتظار الاعتماد.",
    },
    setup: {
      COMPANY: "إنشاء شركة",
      ACCOUNT: "تحميل دليل الحسابات",
      FISCALYEAR: "فتح سنة مالية",
      TAXCODE: "تعريف أكواد الضرائب",
      BRANCH: "إضافة فرع أو موقع",
      WAREHOUSE: "إضافة مخزن",
      CUSTOMER: "إضافة عميل",
      SUPPLIER: "إضافة مورد",
      PRODUCT: "إضافة صنف أو خدمة",
    },
  },
  profile: {
    title: "الملف الشخصي والتفضيلات",
    subtitle: "البيانات الشخصية واللغة والمنطقة الزمنية والأمان.",
    card: "الملف الشخصي",
    displayName: "الاسم الظاهر",
    email: "البريد الإلكتروني",
    locale: "اللغة والمنطقة",
    localeHint:
      "لغة شاشاتك (العربية أو الإنجليزية) وطريقة كتابة الأرقام والتواريخ والمبالغ.",
    notChosen: "لم تُحدَّد بعد ({{language}})",
    timezone: "المنطقة الزمنية",
    timezoneHint: "تُعرض كل التواريخ والأوقات بهذه المنطقة الزمنية.",
    security: "الأمان",
    mfa: "التحقق بخطوتين",
    mfaRule: {
      nobody:
        "اختياري. يُنصح به للمالكين ومسؤولي النظام ومعتمدي العمليات المالية.",
      privileged:
        "إلزامي للمالكين ومسؤولي النظام ومعتمدي العمليات المالية في مساحة العمل هذه.",
      everyone: "إلزامي في مساحة العمل هذه لكل الأعضاء.",
      unknown: "تعذّرت قراءة قاعدة تسجيل الدخول.",
    },
    mfaOn: "مفعّل",
    mfaOff: "غير مفعّل",
    manageMfa: "إدارة التحقق بخطوتين",
    enrollMfa: "تفعيل التحقق بخطوتين",
    sessions: "الجلسات النشطة",
    device: "الجهاز",
    deviceOn: "{{browser}} على {{os}}",
    deviceUnknownBrowser: "متصفح غير معروف على {{os}}",
    deviceUnknown: "جهاز غير معروف",
    ip: "عنوان الشبكة",
    lastActive: "آخر نشاط",
    current: "الحالية",
    revoke: "إنهاء الجلسة",
  },
  inbox: {
    title: "صندوق الوارد",
    subtitle: "الاعتمادات والمهام المسندة إليك وما تجاوز مهلة الإنجاز.",
    tabs: {
      actionRequired: "بانتظار إجرائك",
      waitingOnOthers: "بانتظار الآخرين",
      updates: "التحديثات",
    },
    pendingTasks_zero: "لا توجد مهام معلّقة",
    pendingTasks_one: "مهمة واحدة معلّقة",
    pendingTasks_two: "مهمتان معلّقتان",
    pendingTasks_few: "{{count}} مهام معلّقة",
    pendingTasks_many: "{{count}} مهمة معلّقة",
    pendingTasks_other: "{{count}} مهمة معلّقة",
    columns: {
      task: "المهمة",
      type: "النوع",
      requestedBy: "مقدّم الطلب",
      received: "تاريخ الاستلام",
      slaDue: "مهلة الإنجاز",
      risk: "درجة المخاطرة",
      actions: "الإجراءات",
    },
    risk: {
      Low: "منخفضة",
      Medium: "متوسطة",
      High: "مرتفعة",
    },
    approve: "اعتماد",
    reject: "رفض",
  },
  notifications: {
    title: "الإشعارات",
    unread_zero: "لا توجد إشعارات غير مقروءة",
    unread_one: "إشعار واحد غير مقروء",
    unread_two: "إشعاران غير مقروءين",
    unread_few: "{{count}} إشعارات غير مقروءة",
    unread_many: "{{count}} إشعاراً غير مقروء",
    unread_other: "{{count}} إشعار غير مقروء",
    markAllRead: "تحديد الكل مقروءاً",
    tabs: {
      all: "الكل",
      unread: "غير المقروءة",
    },
    new: "جديد",
    columns: {
      type: "النوع",
      message: "الرسالة",
      received: "تاريخ الاستلام",
      actions: "الإجراءات",
    },
    markRead: "تحديد مقروءاً",
  },
  search: {
    title: "البحث الشامل",
    subtitle:
      "ابحث في العملاء والموردين والأصناف والأوامر والفواتير والمستندات وأحداث سجل المراجعة.",
    placeholder: "ابحث… (مثلاً: فاتورة ذهب)",
    module: "الوحدة",
    allModules: "كل الوحدات",
    emptyTitle: "اكتب للبحث",
    emptyMessage: "تراعي النتائج صلاحياتك، فلا تظهر لك إلا السجلات المتاحة لك.",
    results_zero: "لا توجد نتائج مطابقة لـ «{{query}}»",
    results_one: "نتيجة واحدة مطابقة لـ «{{query}}»",
    results_two: "نتيجتان مطابقتان لـ «{{query}}»",
    results_few: "{{count}} نتائج مطابقة لـ «{{query}}»",
    results_many: "{{count}} نتيجة مطابقة لـ «{{query}}»",
    results_other: "{{count}} نتيجة مطابقة لـ «{{query}}»",
    noResults: "لا توجد نتائج",
    noResultsHint: "جرّب كلمات أخرى أو غيّر عوامل التصفية.",
  },
  nav: {
    home: "الرئيسية",
    inbox: "صندوق الوارد",
    organization: "المؤسسة",
    structure: "الهيكل التنظيمي",
    legalEntities: "الكيانات القانونية",
    companies: "الشركات",
    branches: "الفروع",
    departments: "الإدارات",
    locations: "المواقع",
    crm: "إدارة علاقات العملاء",
    overview: "نظرة عامة",
    leads: "العملاء المحتملون",
    opportunities: "الفرص البيعية",
    contacts: "جهات الاتصال",
    activities: "الأنشطة",
    campaigns: "الحملات",
    planning: "التخطيط",
    demandForecasts: "توقعات الطلب",
    mrpRuns: "تشغيلات تخطيط احتياجات المواد",
    eCommerce: "التجارة الإلكترونية",
    storefronts: "المتاجر الإلكترونية",
    productFeeds: "ملفات بيانات الأصناف",
    onlineOrders: "الطلبات الإلكترونية",
    compliance: "الامتثال",
    calendar: "التقويم",
    regulatoryReports: "التقارير الرقابية",
    eInvoicing: "الفاتورة الإلكترونية",
    auditFiles: "ملفات المراجعة",
    dataSubjectRequests: "طلبات أصحاب البيانات",
    retentionPolicies: "سياسات الاحتفاظ بالبيانات",
    configuration: "التهيئة",
    governance: "الحوكمة",
    segregationOfDuties: "الفصل بين المهام",
    pendingChanges: "التغييرات المعلقة",
    activatedVersions: "الإصدارات المفعّلة",
    entityBuilder: "منشئ نماذج البيانات",
    formViewDesigner: "مصمم النماذج وطرق العرض",
    finance: "المالية",
    financialDashboard: "لوحة المؤشرات المالية",
    chartOfAccounts: "دليل الحسابات",
    journalEntries: "قيود اليومية",
    generalLedger: "دفتر الأستاذ العام",
    trialBalance: "ميزان المراجعة",
    financialStatements: "القوائم المالية",
    customerInvoices: "فواتير العملاء",
    supplierInvoices: "فواتير الموردين",
    payments: "المدفوعات",
    bankReconciliation: "التسوية البنكية",
    cashManagement: "إدارة النقدية",
    fixedAssets: "الأصول الثابتة",
    taxDashboard: "لوحة مؤشرات الضرائب",
    periodClose: "إقفال الفترة",
    exchangeRates: "أسعار الصرف",
    budgeting: "الموازنات التقديرية",
    consolidation: "توحيد القوائم المالية",
    sales: "المبيعات",
    quotations: "عروض الأسعار",
    salesOrders: "أوامر البيع",
    customers: "العملاء",
    deliveryNotes: "أذون التسليم",
    salesInvoices: "فواتير المبيعات",
    returnsRmas: "مردودات المبيعات",
    priceListsDiscounts: "قوائم الأسعار والخصومات",
    creditLimitReview: "مراجعة حدود الائتمان",
    commissions: "العمولات",
    procurement: "المشتريات",
    purchaseRequests: "طلبات الشراء",
    rfqs: "طلبات عروض الأسعار",
    purchaseOrders: "أوامر الشراء",
    goodsReceipts: "أذون الاستلام",
    suppliers: "الموردون",
    supplierEvaluation: "تقييم الموردين",
    procurementContracts: "عقود الشراء",
    inventory: "المخزون",
    products: "الأصناف",
    stockByLocation: "المخزون حسب الموقع",
    inventoryMovements: "الحركات المخزنية",
    transfers: "التحويلات المخزنية",
    adjustments: "التسويات المخزنية",
    cycleCounts: "الجرد الدوري",
    lotSerialTraceability: "تتبع التشغيلات والأرقام التسلسلية",
    reorderPlanner: "مخطط إعادة الطلب",
    valuationReport: "تقرير تقييم المخزون",
    manufacturing: "التصنيع",
    bomsRoutings: "قوائم المواد ومسارات التصنيع",
    workCenters: "مراكز العمل",
    productionOrders: "أوامر الإنتاج",
    mrpPlanner: "مخطط احتياجات المواد",
    qualityInspections: "فحوصات الجودة",
    hrPayroll: "الموارد البشرية والرواتب",
    employeeDirectory: "دليل الموظفين",
    leaveRequests: "طلبات الإجازة",
    timeAttendance: "الحضور والانصراف",
    payrollRuns: "مسيرات الرواتب",
    payslips: "قسائم الرواتب",
    projects: "المشروعات",
    serviceTickets: "تذاكر الخدمة",
    pos: "نقاط البيع",
    shipments: "الشحنات",
    fleetVehicles: "مركبات الأسطول",
    documents: "المستندات",
    reportsBi: "التقارير وذكاء الأعمال",
    dashboardBuilder: "منشئ لوحات المؤشرات",
    reportBuilder: "منشئ التقارير",
    pivotReportViewer: "عارض التقارير المحورية",
    setup: "الإعداد",
    setupCenter: "مركز الإعداد",
    implementationWizard: "معالج التهيئة",
    auditLogExplorer: "مستكشف سجل المراجعة",
    apiWebhookAdmin: "إدارة تطبيقات الربط وإشعارات الويب",
    userManagement: "إدارة المستخدمين",
    rolesPermissions: "الأدوار الوظيفية والصلاحيات",
    workflowBuilder: "منشئ مسارات العمل",
    approvalMatrixDesigner: "مصمم مصفوفة الاعتمادات",
    countryPackManager: "إدارة حزم الدول",
    industryPackManager: "إدارة حزم القطاعات",
    integrationCenter: "مركز التكامل",
    dataImportCenter: "مركز استيراد البيانات",
    tenantSettings: "إعدادات مساحة العمل",
    securityCenter: "مركز الأمان",
    billingSubscription: "الفوترة والاشتراك",
    marketplace: "سوق الإضافات",
    notifications: "الإشعارات",
    search: "البحث",
    profile: "الملف الشخصي",
  },
  ...screensAr,
  ...controlsAr,
} satisfies Catalog;

/**
 * Arabic strings with no approved copy yet (see the header). Everything not
 * listed is taken from glossary.md, errors.md or voice.md.
 */
export const AR_DRAFT_KEYS: readonly string[] = [
  "shell.primaryNav",
  "shell.toggleNav",
  "shell.commands",
  "shell.themeToLight",
  "shell.themeToDark",
  "shell.noWorkspace",
  "shell.cell",
  "shell.palette.label",
  "shell.palette.placeholder",
  "shell.palette.noResults",
  "session.ended",
  "session.workspaceCreated",
  "session.workspaceCreatedMultiple",
  "session.mfaRequired",
  "session.checkAgain",
  "session.checking",
  "session.stillUnavailable",
  "session.workspaceBlock.provisioning.body",
  "session.workspaceBlock.notActive.title",
  "session.workspaceBlock.notActive.body",
  "auth.login.subtitle",
  "auth.login.email",
  "auth.mfa.title",
  "auth.mfa.subtitle",
  "auth.mfa.codeHelp",
  "auth.mfa.submit",
  "auth.mfa.notAccepted",
  "auth.mfa.lostDevice",
  // The workspace-creation screen (D25): written by engineering in the
  // glossary's terms (مساحة العمل، الكود، العملة).
  "auth.workspace.title",
  "auth.workspace.subtitle",
  "auth.workspace.name",
  "auth.workspace.code",
  "auth.workspace.codeHint",
  "auth.workspace.country",
  "auth.workspace.countryHint",
  "auth.workspace.countriesLoading",
  "auth.workspace.countriesFailed",
  "auth.workspace.noCountries",
  "auth.workspace.chooseCountry",
  "auth.workspace.currency",
  "auth.workspace.currencyHint",
  "auth.workspace.chooseCurrency",
  "auth.workspace.timezone",
  "auth.workspace.timezoneHint",
  "auth.workspace.chooseTimezone",
  "auth.workspace.language",
  "auth.workspace.languageHint",
  "auth.workspace.chooseLanguage",
  "auth.workspace.submit",
  "auth.workspace.submitting",
  "auth.workspace.errors.name",
  "auth.workspace.errors.code",
  "auth.workspace.errors.codeTaken",
  "auth.workspace.errors.country",
  "auth.workspace.errors.currency",
  "auth.workspace.errors.timezone",
  "auth.workspace.errors.language",
  "errors.RATE_LIMITED",
  "errors.fallback",
  // Currencies on amounts and the one-set-of-books picker (CUR-2): written by
  // engineering in the glossary's terms (العملة، الكيان القانوني، الدفاتر).
  "common.chooseCountry",
  "money.othersNotConverted",
  "money.currency",
  "money.chooseCurrency",
  "money.currencyHint",
  "money.allCurrencies",
  "money.fromCurrency",
  "money.toCurrency",
  "books.legalEntity",
  "books.chooseLegalEntity",
  "books.entityOption",
  "books.legalEntityRequired",
  "setup.center.subtitle",
  "setup.center.score",
  "setup.center.blockingErrors",
  "setup.center.warnings",
  "setup.center.categories",
  "setup.center.blockingItems",
  "setup.center.noBlockingItems",
  "setup.phases.TaxConfiguration",
  "setup.messages.noTaxCodes",
  "setup.messages.noApprovalMatrix",
  "setup.messages.openingBalancesNeedAccounts",
  // Navigation entries with no glossary term. Glossary-backed ones (chart of
  // accounts, journal entries, trial balance, branches, warehouses…) are not
  // listed.
  "nav.inbox",
  "nav.organization",
  "nav.crm",
  "nav.leads",
  "nav.opportunities",
  "nav.productFeeds",
  "nav.dataSubjectRequests",
  "nav.configuration",
  "nav.entityBuilder",
  "nav.formViewDesigner",
  "nav.finance",
  "nav.financialDashboard",
  "nav.taxDashboard",
  "nav.consolidation",
  "nav.mrpRuns",
  "nav.mrpPlanner",
  "nav.bomsRoutings",
  "nav.payrollRuns",
  "nav.reportsBi",
  "nav.pivotReportViewer",
  "nav.implementationWizard",
  "nav.apiWebhookAdmin",
  "nav.marketplace",
  "nav.billingSubscription",
  // C7 second slice: written by engineering in the glossary's terms. Not
  // listed because they are approved copy: errors.VALIDATION_FAILED.*
  // (errors.md), records.types.* and the glossary's status table (status.*
  // except Supplier and Product), Save / Approve / Reject (voice.md), and
  // strings identical to an approved key (home.title = nav.home,
  // home.continueSetup = setup.center.continue, home.alerts.provisioning =
  // the provisioning title, auth.signup.password, auth.signup.signIn,
  // auth.verify.code, profile.card, profile.mfa, notifications.title).
  "common.cancel",
  "common.saving",
  "common.create",
  "common.select",
  "common.languageNames.en",
  "common.languageNames.ar",
  "meta.title",
  "meta.description",
  "ui.breadcrumb",
  "ui.howToFix",
  "ui.requiredNote",
  "crash.title",
  "crash.body",
  "resource.loadFailed",
  "status.Supplier.Active",
  "status.Supplier.Blocked",
  "status.Supplier.Dormant",
  "status.Product.Draft",
  "status.Product.Active",
  "status.Product.Obsolete",
  "auth.signup.title",
  "auth.signup.subtitle",
  "auth.signup.name",
  "auth.signup.email",
  "auth.signup.emailHint",
  "auth.signup.passwordHint",
  "auth.signup.submit",
  "auth.signup.submitting",
  "auth.signup.haveAccount",
  "auth.verify.title",
  "auth.verify.subtitle",
  "auth.verify.done",
  "auth.verify.submit",
  "auth.verify.submitting",
  // W2.5: engineering's draft of the resend action and the sign-up limit.
  "auth.verify.resendTitle",
  "auth.verify.resendHint",
  "auth.verify.resendEmail",
  "auth.verify.resendSubmit",
  "auth.verify.resendSending",
  "auth.verify.resendRetry",
  "auth.verify.resendSentBadge",
  "auth.verify.resendSent",
  "auth.verify.resendLimited",
  "auth.signup.limited",
  "home.readiness",
  "home.blockingItems",
  "home.newSalesOrder",
  "home.pendingApprovals",
  "home.recentRecords",
  "home.columns.type",
  "home.columns.record",
  "home.columns.submittedBy",
  "home.columns.date",
  "home.columns.number",
  "home.columns.status",
  "home.columns.total",
  "home.kpis.revenue",
  "home.kpis.revenueMeta",
  "home.kpis.receivables",
  "home.kpis.nothingOverdue",
  "home.kpis.overdue",
  "home.kpis.commitments",
  "home.kpis.commitmentsMeta",
  "home.kpis.openSalesOrders",
  "home.kpis.openSalesOrdersMeta",
  "home.alerts.overdueInvoices",
  "home.alerts.approvalsPending",
  "home.setup.COMPANY",
  "home.setup.ACCOUNT",
  "home.setup.FISCALYEAR",
  "home.setup.TAXCODE",
  "home.setup.BRANCH",
  "home.setup.WAREHOUSE",
  "home.setup.CUSTOMER",
  "home.setup.SUPPLIER",
  "home.setup.PRODUCT",
  "profile.title",
  "profile.subtitle",
  "profile.displayName",
  "profile.email",
  "profile.locale",
  "profile.localeHint",
  "profile.notChosen",
  "profile.timezone",
  "profile.timezoneHint",
  "profile.security",
  "profile.mfaRule.nobody",
  "profile.mfaRule.privileged",
  "profile.mfaRule.everyone",
  "profile.mfaRule.unknown",
  "profile.mfaOn",
  "profile.mfaOff",
  "profile.manageMfa",
  "profile.enrollMfa",
  "profile.sessions",
  "profile.device",
  "profile.deviceOn",
  "profile.deviceUnknownBrowser",
  "profile.deviceUnknown",
  "profile.ip",
  "profile.lastActive",
  "profile.current",
  "profile.revoke",
  "inbox.title",
  "inbox.subtitle",
  "inbox.tabs.actionRequired",
  "inbox.tabs.waitingOnOthers",
  "inbox.tabs.updates",
  "inbox.pendingTasks",
  "inbox.columns.task",
  "inbox.columns.type",
  "inbox.columns.requestedBy",
  "inbox.columns.received",
  "inbox.columns.slaDue",
  "inbox.columns.risk",
  "inbox.columns.actions",
  "inbox.risk.Low",
  "inbox.risk.Medium",
  "inbox.risk.High",
  "notifications.unread",
  "notifications.markAllRead",
  "notifications.tabs.all",
  "notifications.tabs.unread",
  "notifications.new",
  "notifications.columns.type",
  "notifications.columns.message",
  "notifications.columns.received",
  "notifications.columns.actions",
  "notifications.markRead",
  "search.title",
  "search.subtitle",
  "search.placeholder",
  "search.module",
  "search.allModules",
  "search.emptyTitle",
  "search.emptyMessage",
  "search.results",
  "search.noResults",
  "search.noResultsHint",
  // C7-REST: the statuses of record types the glossary's status table does
  // not cover yet (it covers Workspace, AccountingPeriod and
  // InventoryMovement, which are not listed), and three record types with no
  // glossary term. The launch screens' own drafts follow from
  // ./screens.ar.ts.
  "status.LegalEntity.Provisional",
  "status.LegalEntity.Active",
  "status.LegalEntity.Inactive",
  "status.Company.Draft",
  "status.Company.Active",
  "status.Company.Closed",
  "status.Branch.Active",
  "status.Branch.Inactive",
  "status.Location.Active",
  "status.Location.Inactive",
  "status.BankStatement.Uploaded",
  "status.BankStatement.Matched",
  "status.BankStatement.Posted",
  "status.FixedAsset.Draft",
  "status.FixedAsset.Active",
  "status.FixedAsset.Depreciated",
  "status.FixedAsset.Disposed",
  "status.FixedAsset.Impairment",
  "status.TaxReturn.Draft",
  "status.TaxReturn.Filed",
  "status.TaxReturn.Overdue",
  "status.TaxReturn.Paid",
  "status.Budget.Draft",
  "status.Budget.Active",
  "status.Budget.Superseded",
  "status.ConsolidationGroup.NotRun",
  "status.ConsolidationGroup.Completed",
  "status.ConsolidationGroup.Failed",
  "status.IntercompanyItem.Open",
  "status.IntercompanyItem.Matched",
  "status.IntercompanyItem.Settled",
  "status.DeliveryNote.Draft",
  "status.DeliveryNote.Picking",
  "status.DeliveryNote.Packed",
  "status.DeliveryNote.Shipped",
  "status.DeliveryNote.Delivered",
  "status.DeliveryNote.Partial",
  "status.DeliveryNote.Returned",
  "status.SalesReturn.Requested",
  "status.SalesReturn.Approved",
  "status.SalesReturn.Received",
  "status.SalesReturn.Rejected",
  "status.SalesReturn.Credited",
  "status.SalesReturn.Replaced",
  "status.Discount.Active",
  "status.Discount.Draft",
  "status.Discount.Superseded",
  "status.CreditLimitRequest.Pending",
  "status.CreditLimitRequest.Approved",
  "status.CreditLimitRequest.Declined",
  "status.Commission.Accrued",
  "status.Commission.Approved",
  "status.Commission.Paid",
  "status.PurchaseRequest.Draft",
  "status.PurchaseRequest.Submitted",
  "status.PurchaseRequest.Approved",
  "status.PurchaseRequest.Rejected",
  "status.PurchaseRequest.Converted",
  "status.PurchaseRequest.Cancelled",
  "status.Rfq.Draft",
  "status.Rfq.Sent",
  "status.Rfq.QuotesReceived",
  "status.Rfq.Awarded",
  "status.Rfq.Cancelled",
  "status.ProcurementContract.Active",
  "status.ProcurementContract.Expiring",
  "status.ProcurementContract.Expired",
  "status.ProcurementContract.Draft",
  "status.ProcurementContract.Cancelled",
  "status.StockTransfer.Draft",
  "status.StockTransfer.InTransit",
  "status.StockTransfer.Received",
  "status.StockTransfer.Posted",
  "status.StockTransfer.Cancelled",
  "status.StockAdjustment.Draft",
  "status.StockAdjustment.PendingApproval",
  "status.StockAdjustment.Approved",
  "status.StockAdjustment.Posted",
  "status.StockAdjustment.Rejected",
  "status.CycleCount.Draft",
  "status.CycleCount.InProgress",
  "status.CycleCount.Reviewed",
  "status.CycleCount.Approved",
  "status.CycleCount.Posted",
  "status.ApiClient.Active",
  "status.ApiClient.Revoked",
  "status.OutboxMessage.Pending",
  "status.OutboxMessage.Sending",
  "status.OutboxMessage.Sent",
  "status.OutboxMessage.DeadLetter",
  "status.TenantUser.Invited",
  "status.TenantUser.Active",
  "status.TenantUser.Suspended",
  "status.TenantUser.Removed",
  "status.Role.Draft",
  "status.Role.Active",
  "status.Role.Superseded",
  "status.EntityDefinition.Draft",
  "status.EntityDefinition.Validated",
  "status.EntityDefinition.Simulated",
  "status.EntityDefinition.Approved",
  "status.EntityDefinition.Active",
  "status.EntityDefinition.Superseded",
  "status.ViewDefinition.Draft",
  "status.ViewDefinition.Active",
  "status.ViewDefinition.Superseded",
  "status.LayoutDefinition.Draft",
  "status.LayoutDefinition.Active",
  "status.ApprovalMatrix.Draft",
  "status.ApprovalMatrix.Active",
  "status.ApprovalMatrix.Superseded",
  "status.Connector.Connected",
  "status.Connector.Error",
  "status.Connector.NotConfigured",
  "status.Connector.Disabled",
  "status.ImportJob.Uploaded",
  "status.ImportJob.Validating",
  "status.ImportJob.Previewing",
  "status.ImportJob.DryRun",
  "status.ImportJob.Completed",
  "status.ImportJob.Failed",
  "status.ImportJob.RolledBack",
  "status.Subscription.Active",
  "status.Subscription.PastDue",
  "status.Subscription.Cancelled",
  "status.Subscription.Trial",
  "status.SubscriptionInvoice.Paid",
  "status.SubscriptionInvoice.Open",
  "status.SubscriptionInvoice.Overdue",
  "records.types.Payment",
  "records.types.ProductionOrder",
  "records.types.SalesOrderDelivery",
  ...SCREENS_AR_DRAFT_KEYS,
  ...CONTROLS_AR_DRAFT_KEYS,
];
